"use client";

/**
 * Barre d'action fixe en bas d'écran — MOBILE UNIQUEMENT (`md:hidden`).
 * 3 actions haute-intention pour convertir le trafic mobile (~82 %) : Appeler,
 * WhatsApp, Réserver. Sur desktop, c'est le FAB WhatsApp (Whatsapp.tsx) qui prend
 * le relais — le FAB est masqué en mobile pour éviter un double WhatsApp.
 *
 * Trilingue (libellés fr/en/ar via le chemin), RTL-safe (grid), href réservation
 * localisé (localeNavHref). `env(safe-area-inset-bottom)` pour l'encoche iPhone.
 */
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Icon } from "@/components/ui/Icon";
import { whatsappLink } from "@/config/infos";
import { COMPANY } from "@/config/company";
import { localeFromPath } from "@/config/i18n";
import { localeNavHref } from "@/config/localeRoutes";

const LABELS = {
  fr: { call: "Appeler", whatsapp: "WhatsApp", book: "Réserver" },
  en: { call: "Call", whatsapp: "WhatsApp", book: "Book" },
  ar: { call: "اتصل", whatsapp: "واتساب", book: "احجز" },
} as const;

export function MobileActionBar(): JSX.Element {
  const pathname = usePathname() ?? "/";
  const locale = localeFromPath(pathname);
  const t = LABELS[locale];
  const reserveHref = localeNavHref("/reservation", locale);

  const cell =
    "flex flex-col items-center justify-center gap-0.5 py-2.5 text-xs font-semibold";

  return (
    <nav
      aria-label={t.book}
      className="fixed inset-x-0 bottom-0 z-[55] grid grid-cols-3 border-t border-black/10 bg-white/95 text-black shadow-[0_-6px_20px_rgba(0,0,0,0.08)] backdrop-blur md:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <a href={`tel:${COMPANY.phoneIntl}`} className={cell}>
        <Icon icon="ph:phone-fill" className="h-5 w-5 text-primary" />
        {t.call}
      </a>
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className={`${cell} border-x border-black/10`}
      >
        <Icon icon="mdi:whatsapp" className="h-5 w-5 text-[#25D366]" />
        {t.whatsapp}
      </a>
      <Link href={reserveHref} className={`${cell} bg-primary`}>
        <Icon icon="mdi:calendar-check" className="h-5 w-5 text-black" />
        {t.book}
      </Link>
    </nav>
  );
}

export default MobileActionBar;
